import React, { useState } from "react";
import "./BatchDetails.css";
import DataTable from "./DataTable";
import BatchViewer from "../Components/Viewers/BatchViewer";
import { batchDetailsAPI, facultyAPI, funcAreaAPI, trngCourseAPI } from "./api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function BatchDetails() {
  const [batchSrNo, setBatchSrNo] = useState("");
  const [batchInfo, setBatchInfo] = useState(null);
  const [facultyNames, setFacultyNames] = useState("");
  const [funcSubAreaName, setFuncSubAreaName] = useState("");
  const [trngCourseName, setTrngCourseName] = useState("");
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showTable, setShowTable] = useState(false);

  const handleBatchSelect = (row) => {
    setBatchSrNo(String(row.batch_sr_no ?? ""));
    setShowViewer(false);
  };

  const handleSubmit = async () => {
    const srNo = String(batchSrNo).trim();
    if (!srNo) {
      setErrorMsg("Please enter or select a Batch Sr.No.");
      return;
    }
    setErrorMsg(null);
    setLoading(true);
    setShowTable(false);
    try {
      const [batchRow, participantData] = await Promise.all([
        batchDetailsAPI.getBatch(srNo),
        batchDetailsAPI.getParticipants(srNo),
      ]);
      setBatchInfo(batchRow);
      setParticipants(Array.isArray(participantData) ? participantData : []);
      console.log('[BatchDetails] batchRow:', batchRow);

      // Fetch all faculty names (up to 5 slots)
      const facultyCodes = [batchRow?.faculty_code, batchRow?.faculty_code_1, batchRow?.faculty_code_2, batchRow?.faculty_code_3, batchRow?.faculty_code_4].filter(Boolean);
      if (facultyCodes.length) {
        try {
          const allFaculty = await facultyAPI.getAll();
          const facMap = {};
          (Array.isArray(allFaculty) ? allFaculty : []).forEach(f => { facMap[String(f.faculty_code)] = f.faculty_name; });
          setFacultyNames(facultyCodes.map(c => facMap[String(c)] || String(c)).join(', '));
        } catch {
          setFacultyNames(facultyCodes.join(', '));
        }
      } else {
        setFacultyNames("");
      }

      // Fetch functional area name
      if (batchRow?.func_sub_area_code) {
        try {
          const funcAreaData = await funcAreaAPI.getAll({ func_sub_area_code: batchRow.func_sub_area_code });
          const funcArea = Array.isArray(funcAreaData) ? funcAreaData[0] : null;
          setFuncSubAreaName(funcArea?.func_sub_area_name ?? batchRow.func_sub_area_code);
        } catch {
          setFuncSubAreaName(batchRow.func_sub_area_code);
        }
      } else {
        setFuncSubAreaName("");
      }

      // Fetch training course name
      if (batchRow?.trng_course_code) {
        try {
          const trngCourseData = await trngCourseAPI.getAll({ trng_course_code: String(batchRow.trng_course_code) });
          console.log('[BatchDetails] trng_course_code:', batchRow.trng_course_code, '| trngCourseData:', trngCourseData);
          const trngCourse = Array.isArray(trngCourseData) && trngCourseData.length > 0 ? trngCourseData[0] : null;
          console.log('[BatchDetails] resolved trngCourse:', trngCourse);
          setTrngCourseName(trngCourse?.trng_course_name ?? String(batchRow.trng_course_code));
        } catch (e) {
          console.error('[BatchDetails] trngCourseAPI error:', e);
          setTrngCourseName(String(batchRow.trng_course_code));
        }
      } else {
        setTrngCourseName("");
      }

      setShowTable(true);
    } catch {
      setErrorMsg("Failed to load batch details.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBatchSrNo("");
    setBatchInfo(null);
    setFacultyNames("");
    setFuncSubAreaName("");
    setTrngCourseName("");
    setParticipants([]);
    setShowTable(false);
    setErrorMsg(null);
  };

  const numDays = batchInfo?.start_date && batchInfo?.end_date
    ? Math.floor((new Date(batchInfo.end_date) - new Date(batchInfo.start_date)) / (1000 * 60 * 60 * 24)) + 1
    : "";

  const participantColumns = [
    { header: "Employee Name", accessor: (row) => row.emp_name ?? "" },
    { header: "Sal Code", accessor: (row) => row.sal_code ?? "" },
    { header: "Division", accessor: (row) => row.division ?? "" },
    { header: "Attended", accessor: (row) => row.attended_yn ?? "" },
    { header: "Location", accessor: (row) => row.locn || row.branch || "" },
  ];

  const handleExportExcel = () => {
    const today = new Date().toLocaleDateString("en-GB");
    const startDate = batchInfo?.start_date ? new Date(batchInfo.start_date).toLocaleDateString("en-GB") : "";
    const endDate = batchInfo?.end_date ? new Date(batchInfo.end_date).toLocaleDateString("en-GB") : "";

    const aoa = [
      ["Report Title : Batch Details"],
      [`Report Date ${today}`],
      [],
      ["Batch Wise Attendance Details"],
      [],
      ["Functional Area", funcSubAreaName, "Training Course Name", trngCourseName],
      ["Start Date(DD/MM/YY)", startDate, "End Date(DD/MM/YY)", endDate],
      ["Start Time (hh:mm)", batchInfo?.start_time ?? "", "End Time (hh:mm)", batchInfo?.end_time ?? ""],
      ["No of Days", numDays, "Faculty", facultyNames],
      ["Total Faculty Cost", batchInfo?.total_faculty_cost ?? "", "Venue", batchInfo?.venue ?? ""],
      ["Batch Size", batchInfo?.batch_size ?? ""],
      [],
      ["Name", "Salary Code", "Location", "Division", "Attended"],
      ...participants.map((row) => [
        row.emp_name ?? "",
        row.sal_code ?? "",
        row.locn || row.branch || "",
        row.division ?? "",
        row.attended_yn ?? "",
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BatchDetails");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `BatchDetails.xlsx`);
  };

  return (
    <div className="bd-container">

      {/* Input row */}
      <div className="bd-input-row">
        <label className="bd-label">Batch Sr.No.</label>
        <input
          type="text"
          className="bd-input"
          value={batchSrNo}
          onChange={(e) => setBatchSrNo(e.target.value)}
        />
        <button className="btn btn-outline" type="button" onClick={() => setShowViewer(true)}>Get</button>
        <button className="btn btn-secondary" type="button" onClick={handleReset}>Reset</button>
      </div>

      {/* Action row */}
      <div className="action-row">
        <button className="btn btn-outline" onClick={handleSubmit} disabled={loading}>
          {loading ? "Loading…" : "Submit"}
        </button>
      </div>

      {errorMsg && (
        <p className="bd-error">{errorMsg}</p>
      )}

      {/* Details section */}
      {showTable && (
        <div className="bd-details">

          {/* Batch info table */}
          <p className="bd-section-title">Batch Wise Attendence Details</p>
          <table className="bd-info-table">
            <tbody>
              <tr>
                <td className="bd-td-label">Functional Area</td>
                <td>{funcSubAreaName}</td>
                <td className="bd-td-label">Training Course Name</td>
                <td>{trngCourseName}</td>
              </tr>
              <tr>
                <td className="bd-td-label">Start Date (DD/MM/YY)</td>
                <td>{batchInfo?.start_date ? new Date(batchInfo.start_date).toLocaleDateString("en-GB") : ""}</td>
                <td className="bd-td-label">End Date (DD/MM/YY)</td>
                <td>{batchInfo?.end_date ? new Date(batchInfo.end_date).toLocaleDateString("en-GB") : ""}</td>
              </tr>
              <tr>
                <td className="bd-td-label">Start Time (hh:mm)</td>
                <td>{batchInfo?.start_time ?? ""}</td>
                <td className="bd-td-label">End Time (hh:mm)</td>
                <td>{batchInfo?.end_time ?? ""}</td>
              </tr>
              <tr>
                <td className="bd-td-label">No. of Days</td>
                <td>{numDays}</td>
                <td className="bd-td-label">Faculty</td>
                <td>{facultyNames}</td>
              </tr>
              <tr>
                <td className="bd-td-label">Total Faculty Cost</td>
                <td>{batchInfo?.total_faculty_cost ?? ""}</td>
                <td className="bd-td-label">Venue</td>
                <td>{batchInfo?.venue ?? ""}</td>
              </tr>
              <tr>
                <td className="bd-td-label">Batch Size</td>
                <td>{batchInfo?.batch_size ?? ""}</td>
                <td className="bd-td-label">Status</td>
                <td>{batchInfo?.trng_status ?? ""}</td>
              </tr>
            </tbody>
          </table>

          {/* Participants table */}
          <p className="bd-section-title" style={{ marginTop: 20 }}>Participants</p>
          <DataTable
            columns={participantColumns}
            data={participants}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => {}}
            onChangeEditCell={() => {}}
            selectedForDelete={{}}
            onToggleDeleteRow={() => {}}
            emptyMessage="No participants found for this batch"
            rowKey={(row, idx) => `${row.sal_code}-${idx}`}
          />
          {participants.length > 0 && (
            <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
              <button className="btn btn-outline" onClick={handleExportExcel}>Export Excel</button>
            </div>
          )}
        </div>
      )}

      {/* BatchViewer modal */}
      {showViewer && (
        <div className="facviewer-overlay" onClick={() => setShowViewer(false)}>
          <div className="facviewer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="facviewer-close" onClick={() => setShowViewer(false)}>&times;</button>
            <BatchViewer selectable onSelect={handleBatchSelect} loadTrigger={showViewer} />
          </div>
        </div>
      )}
    </div>
  );
}

export default BatchDetails;